import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import type { AuthContextValue } from "@/hooks/useAuth";

/**
 * Capa de protección del frontend (UI).
 * NOTA IMPORTANTE: esto es solo una capa de conveniencia. La seguridad real
 * vive en PostgreSQL (RLS + funciones SECURITY DEFINER). Aunque un usuario
 * escriba manualmente /admin o /super-admin, la base de datos rechaza
 * cualquier operación no autorizada.
 */

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background-50">
      <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
    </div>
  );
}

function SuspendedScreen({ onSignOut }: { onSignOut: () => Promise<void> }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-background-50 px-4 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100">
        <i className="ri-forbid-2-line text-3xl text-accent-600"></i>
      </span>
      <div>
        <h1 className="font-heading text-xl font-bold text-foreground-950">
          Tu cuenta ha sido suspendida
        </h1>
        <p className="mt-2 max-w-sm text-sm text-foreground-600">
          Tu cuenta ha sido suspendida. Contacta con soporte para más
          información.
        </p>
      </div>
      <button
        type="button"
        onClick={onSignOut}
        className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
      >
        Cerrar sesión
      </button>
    </div>
  );
}

interface GuardProps {
  children: ReactNode;
  allowed?: (auth: AuthContextValue) => boolean;
  redirectTo?: string;
}

function Guard({ children, allowed, redirectTo = "/" }: GuardProps) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.loading) {
    return <LoadingScreen />;
  }

  if (!auth.user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (auth.isBlocked) {
    return <SuspendedScreen onSignOut={auth.signOut} />;
  }

  if (allowed && !allowed(auth)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

/** Cualquier usuario autenticado (y no bloqueado). */
export default function ProtectedRoute({ children }: { children: ReactNode }) {
  return <Guard>{children}</Guard>;
}

/** Solo administradores (admin o super_admin). */
export function AdminRoute({ children }: { children: ReactNode }) {
  return (
    <Guard
      allowed={(a) => a.role === "admin" || a.role === "super_admin"}
    >
      {children}
    </Guard>
  );
}

/** Solo el super administrador. */
export function SuperAdminRoute({ children }: { children: ReactNode }) {
  return <Guard allowed={(a) => a.role === "super_admin"}>{children}</Guard>;
}

/** Vendedores aprobados o administradores. */
export function SellerRoute({ children }: { children: ReactNode }) {
  return (
    <Guard
      allowed={(a) =>
        a.sellerStatus === "approved" ||
        a.role === "admin" ||
        a.role === "super_admin"
      }
      redirectTo="/perfil/solicitud-vendedor"
    >
      {children}
    </Guard>
  );
}